import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import type { DataTableColumn } from '@/components/ui/data-table-customized'
import {
    TicketsByEmployeeAndType,
    TicketsByType,
    TicketTypeGroup,
} from '@/modules/Ticket/types/response'
import EscalatedTicketsDialog from './EscalatedTicketsDialog'

interface TicketProcessingTableProps {
    ticketsByEmployeeAndType?: TicketsByEmployeeAndType[]
    // Same cohort as the table, but counted once per ticket instead of once per
    // assignee. Used only to show how far the Total row sits from the real
    // ticket count — see the total column's tooltip.
    ticketsByType?: TicketsByType[]
    startDate?: string
    endDate?: string
}

// Define the order of ticket type groups to display
const TICKET_TYPE_GROUPS: TicketTypeGroup[] = [
    'support_request',
    'error_service',
    'service_request',
    'service',
    'meeting',
    'change',
    'internal',
]

// Transform data to flat structure for DataTable
interface TicketProcessingRow {
    employee_id: number
    employee_name: string
    support_request: number
    error_service: number
    service_request: number
    service: number
    meeting: number
    change: number
    internal: number
    // Declared explicitly rather than derived from ticket_counts: that loop
    // silently drops any group missing from TICKET_TYPE_GROUPS, and this column
    // must not inherit that failure mode.
    escalated_tickets: number
    total: number
    isTotal?: boolean
}

const TicketProcessingTable: React.FC<TicketProcessingTableProps> = ({
    ticketsByEmployeeAndType = [],
    ticketsByType = [],
    startDate = '',
    endDate = '',
}) => {
    // `t` stays on the global namespace so the existing column titles keep
    // resolving to the same strings; `tTicket` reads the module locale, where
    // this feature's wording lives. Merging the two would silently rename
    // employee-name, which differs between the namespaces.
    const { t } = useTranslation()
    const { t: tTicket } = useTranslation('ticket')
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(10)
    const [detailTarget, setDetailTarget] = useState<{
        id: number
        name: string
    } | null>(null)

    // Transform data to flat row structure
    const rows = useMemo<TicketProcessingRow[]>(() => {
        const dataRows = ticketsByEmployeeAndType.map((employee) => {
            const row: TicketProcessingRow = {
                employee_id: employee.employee_id,
                employee_name: employee.employee_name,
                support_request: 0,
                error_service: 0,
                service_request: 0,
                service: 0,
                meeting: 0,
                change: 0,
                internal: 0,
                // Optional on the API type: an older backend build must read as
                // 0 here rather than propagate undefined into the rate.
                escalated_tickets: employee.escalated_tickets ?? 0,
                total: employee.total_tickets,
            }

            employee.ticket_counts.forEach((count) => {
                if (count.ticket_type_group in row) {
                    row[count.ticket_type_group as TicketTypeGroup] =
                        count.total_tickets
                }
            })

            return row
        })

        // Add total row
        if (dataRows.length > 0) {
            const totalRow: TicketProcessingRow = {
                employee_id: -1,
                employee_name: t('total'),
                support_request: 0,
                error_service: 0,
                service_request: 0,
                service: 0,
                meeting: 0,
                change: 0,
                internal: 0,
                escalated_tickets: 0,
                total: 0,
                isTotal: true,
            }

            dataRows.forEach((row) => {
                TICKET_TYPE_GROUPS.forEach((group) => {
                    totalRow[group] += row[group]
                })
                totalRow.escalated_tickets += row.escalated_tickets
                totalRow.total += row.total
            })

            dataRows.push(totalRow)
        }

        return dataRows
    }, [ticketsByEmployeeAndType, t])

    // The Total row sums per-employee totals, so a ticket with two technical
    // assignees is counted twice and a ticket with none is not counted at all.
    // On staging that gap is 385 against 672 real tickets, because 292 of them
    // have no technical assignee. The physical figure is shown alongside rather
    // than replacing the sum: the sum is what the per-employee rows add up to,
    // and it stays the denominator of the escalation rate.
    const physicalTotal = useMemo(
        () => ticketsByType.reduce((sum, group) => sum + group.group_total, 0),
        [ticketsByType]
    )

    // Create columns dynamically
    const columns = useMemo<DataTableColumn<TicketProcessingRow>[]>(() => {
        const cols: DataTableColumn<TicketProcessingRow>[] = [
            {
                key: 'employee_name',
                title: t('employee-name'),
                width: 200,
                pinned: 'left',
                render: (value, row) => (
                    <span
                        className={row.isTotal ? 'font-semibold uppercase' : ''}
                    >
                        {value}
                    </span>
                ),
            },
            ...TICKET_TYPE_GROUPS.map((group) => ({
                key: group,
                title: t(`ticket-group.${group}`),
                width: 120,
                cellClassName: 'text-center',
                render: (value: number, row: TicketProcessingRow) => (
                    <span
                        className={
                            row.isTotal
                                ? 'font-semibold'
                                : value > 0
                                  ? 'text-gray-800'
                                  : 'text-gray-400'
                        }
                    >
                        {value}
                    </span>
                ),
            })),
            {
                key: 'escalated_tickets',
                title: tTicket('escalated.column'),
                width: 140,
                cellClassName: 'text-center',
                render: (value: number, row: TicketProcessingRow) => {
                    // Zero-safe, and on the total row this is already the
                    // weighted rate SUM(escalated)/SUM(total) rather than an
                    // average of the per-employee rates.
                    const rate = row.total > 0 ? (value / row.total) * 100 : 0
                    const label = (
                        <>
                            {value}
                            <span className="ml-1 text-xs text-gray-500">
                                ({rate.toFixed(1)}%)
                            </span>
                        </>
                    )

                    // The total row carries employee_id -1, so it must be
                    // excluded by more than "value > 0" or clicking it would
                    // query the API for employee -1.
                    if (row.isTotal || value <= 0) {
                        return (
                            <span
                                className={
                                    row.isTotal
                                        ? 'font-semibold'
                                        : 'text-gray-400'
                                }
                                title={
                                    row.isTotal
                                        ? undefined
                                        : tTicket('escalated.column-tooltip')
                                }
                            >
                                {label}
                            </span>
                        )
                    }

                    return (
                        <button
                            type="button"
                            // Same treatment as every other clickable cell in
                            // the app. A dotted underline is spoken for here —
                            // it pairs with cursor-help to mean "hover for an
                            // explanation", which is the opposite of an action.
                            className="text-blue-600 hover:underline"
                            aria-label={tTicket('escalated.open-detail', {
                                name: row.employee_name,
                            })}
                            onClick={() =>
                                setDetailTarget({
                                    id: row.employee_id,
                                    name: row.employee_name,
                                })
                            }
                        >
                            {label}
                        </button>
                    )
                },
            },
            {
                key: 'total',
                title: t('total'),
                width: 100,
                cellClassName: 'text-center',
                render: (value: number, row: TicketProcessingRow) => (
                    <span
                        className={
                            row.isTotal
                                ? 'font-bold text-blue-700'
                                : 'font-medium text-blue-600'
                        }
                        title={
                            row.isTotal && physicalTotal > 0
                                ? tTicket('escalated.total-row-tooltip', {
                                      attribution: value,
                                      physical: physicalTotal,
                                  })
                                : undefined
                        }
                    >
                        {value}
                    </span>
                ),
            },
        ]
        return cols
    }, [t, tTicket, physicalTotal])

    // Get row className for total row styling
    const getRowClassName = (row: TicketProcessingRow) => {
        return row.isTotal ? 'bg-gray-100' : ''
    }

    // min-w-0 because this is a grid item: the default min-width:auto stops it
    // shrinking below the table's intrinsic width, and min-width beats the
    // track size, so the whole row overflows the page instead of the table
    // scrolling inside it. The Escalated column added 140px on top of the eight
    // existing ones, which is what pushed this over the edge.
    return (
        <div className="bg-white rounded-md p-4 col-span-12 flex flex-col min-w-0">
            <Typogaphy size="small" className="text-primary font-semibold mb-2">
                {t('ticket-processing-by-employee')}
            </Typogaphy>

            <DataTableCustomized
                columns={columns}
                rows={rows}
                rowKey={(row) => String(row.employee_id)}
                rowClassName={getRowClassName}
                size="small"
                emptyMessage={t('no-data')}
                pagination={{
                    page,
                    pageSize,
                    total: rows.length,
                    pageSizeOptions: [10, 20, 50],
                    onPageChange: setPage,
                    onPageSizeChange: (size) => {
                        setPageSize(size)
                        setPage(1)
                    },
                }}
            />

            <p className="mt-2 text-xs text-gray-500">
                {tTicket('escalated.provenance-note')}
            </p>

            {detailTarget && (
                <EscalatedTicketsDialog
                    employeeId={detailTarget.id}
                    employeeName={detailTarget.name}
                    startDate={startDate}
                    endDate={endDate}
                    open={Boolean(detailTarget)}
                    onClose={() => setDetailTarget(null)}
                />
            )}
        </div>
    )
}

export default TicketProcessingTable
